import {
  of,
  map,
  tap,
  take,
  from,
  EMPTY,
  retry,
  timer,
  filter,
  Subject,
  catchError,
  forkJoin,
  switchMap,
  Observable,
  Subscription,
  firstValueFrom,
  BehaviorSubject,
} from "rxjs";
import {
  RoleModel,
  RoleTypeEnum,
  RoleAccessModel,
  RoleModuleAccessModel,
  RoleMasterAccessListModel,
} from "src/app/store/models/role.model";
import {
  Tab,
  UserModel,
  UserSource,
  UserRolesModel,
  EditorUserModel,
  MapUsersByGroup,
  PlantAssignedModel,
  RegionAssignedModel,
  // RegionAssignedModel,
  CountryAccessListModel,
} from "src/app/store/models/user.model";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { inject, Injectable } from "@angular/core";
import { collection } from "src/app/utils/collection.util";
import { USER_MENU } from "src/app/store/actions/modules.action";
import { StorageService } from "../storage-service/storage.service";
import { NetworkService } from "../network-service/network.service";
import { FirebaseService } from "../firebase-service/firebase.service";
import { ADD_USER, UPDATE_USER } from "src/app/store/actions/user.action";
import { ModulesWithSubmodulesModel } from "src/app/store/models/module.model";

@Injectable({ providedIn: "root" })
export class UserService {
  private router = inject(Router);
  networkService = inject(NetworkService);
  private storage = inject(StorageService);
  private firebaseService = inject(FirebaseService);
  private store = inject(Store<{ user: UserModel }>);

  regionSub?: Subscription;
  _destroy$ = new Subject<void>();
  userSubscription: Subscription | null = null;
  userFilterByCountry: Subscription | null = null;
  allUsersSubscription: Subscription | null = null;
  reportingUser = new BehaviorSubject<UserModel>({});
  public editUserId = new BehaviorSubject<string>("");
  iscountryChange = new BehaviorSubject<boolean>(false);
  totalSelectedFilterCount = new BehaviorSubject<number>(0);
  public navigateBack = new BehaviorSubject<boolean>(false);
  public isSiteChange = new BehaviorSubject<boolean>(false);
  public filterUsersSubscription: Subscription | null = null;
  public isCountryChange = new BehaviorSubject<boolean>(false);
  public isFilterModalOpen = new BehaviorSubject<boolean>(false);
  public isRefreshUserSubject = new BehaviorSubject<boolean>(false);
  selectedPermissionsSubject = new BehaviorSubject<UserSource[]>([]);
  public isAddUserModalOpenSubject = new BehaviorSubject<boolean>(false);
  public isEditUserModalOpenSubject = new BehaviorSubject<boolean>(false);

  getAllBogProUsersTrigger$ = new Subject<{
    country: string[];
    sites: string[];
  }>();

  public mappedUsers = new BehaviorSubject<MapUsersByGroup>({
    noPermission: [],
    fieldEditor: [],
    structuralEditor: [],
  });

  allTabs: Tab[] = [
    {
      id: "dashboard",
      title: "dashboard",
      route: "/dashboard",
      inactiveIcon: "../../assets/tab-icons/dashboard.svg",
      activeIcon: "../../assets/tab-icons/dashboard-active.svg",
      order: 0,
    },
    {
      id: "process_safety_assesment",
      title: "assessment",
      route: "/assessment",
      inactiveIcon: "../../assets/tab-icons/assessment.svg",
      activeIcon: "../../assets/tab-icons/assessment-active.svg",
      order: 0,
    },
    {
      id: "ei",
      title: "energy-isolation",
      route: "/energy-isolation",
      inactiveIcon: "../../assets/tab-icons/energy.svg",
      activeIcon: "../../assets/tab-icons/energy-active.svg",
      order: 0,
    },
    {
      id: "action_management",
      title: "action-management",
      route: "/action-management",
      inactiveIcon: "../../assets/tab-icons/action-management.svg",
      activeIcon: "../../assets/tab-icons/action-management-active.svg",
      order: 0,
    },
    {
      title: "settings",
      route: "/settings",
      activeIcon: "../../assets/tab-icons/settings-icon-active.svg",
      inactiveIcon: "../../assets/tab-icons/settings-icon.svg",
      id: "settings",
      order: 0,
    },
    {
      title: "admin",
      route: "/admin",
      activeIcon: "../../assets/tab-icons/admin-active.svg",
      inactiveIcon: "../../assets/tab-icons/admin.svg",
      id: "admin",
      order: 0,
    },
  ];
  public rolesCategory = [
    { label: "Flow Role", value: "flow_role" },
    { label: "Viewer Role", value: "viewer_role" },
    { label: "Derived Role", value: "derived_role" },
    { label: "Admin Roles (Customer)", value: "admin" },
    { label: "Platform Admin (Expert Role)", value: "platform" },
  ];

  constructor() {}

  async initializeUser(user: UserModel) {
    if (user && user.email && user.token) {
      // Only one live user listener at a time.
      this.userSubscription?.unsubscribe();
      this.userSubscription = this.firebaseService
        .getUser(user.email)
        .pipe(
          filter((user) => !!user && !!user.email && !!user.token),
          tap((user) => {
            if (this.networkService.getCurrentNetworkStatus()) {
              this.storage.set("user", user);
            }
            if (
              !this.isUser() &&
              this.networkService.getCurrentNetworkStatus()
            ) {
              this.store.dispatch(ADD_USER(user));
            } else {
              if (this.networkService.getCurrentNetworkStatus()) {
                this.store.dispatch(UPDATE_USER(user));
              }
            }
          }),
          // Switch to fetching modules after user is processed
          switchMap(() =>
            this.firebaseService.GetModuleSubmodules().pipe(
              tap({
                next: (modules: ModulesWithSubmodulesModel[]) => {
                  this.storage.set("modules", modules);
                  this.store.dispatch(USER_MENU(modules));
                },
              }),
              // A menu fetch failure keeps the last menu and must not tear
              // down the user listener above it.
              catchError((error) => {
                console.error("[initializeUser] module fetch failed", error);
                return EMPTY;
              }),
            ),
          ),
          // A Firestore error (network blip, token refresh, roles_master read)
          // used to kill this listener for good. Reconnect with backoff instead.
          retry({
            delay: (error, attempt) => {
              console.error(
                `[initializeUser] user listener failed (attempt ${attempt}), retrying`,
                error,
              );
              this.fallbackToStoredUserIfEmpty(user);
              return timer(Math.min(1000 * 2 ** (attempt - 1), 30000));
            },
          }),
        )
        .subscribe();
    }
  }

  // Only used when the very first load failed and the store has no user yet.
  // Never overwrites a user already in the store — that copy carries the last
  // good permissions, and the startup copy may be older or differently shaped.
  private fallbackToStoredUserIfEmpty(user: UserModel): void {
    if (this.isUser()) return;
    this.store.dispatch(ADD_USER(user));
  }

  hasUserAccessToCheckedSite(): Observable<RoleAccessModel> {
    return this.store.select("user").pipe(
      switchMap((user) => {
        const checkedSiteId =
          user?.checkedInLocation?.site?.locationId ??
          user?.defaultLocation?.site?.locationId;

        if (!user || !checkedSiteId) {
          return of({} as RoleAccessModel);
        }
        /* =====================================================
         1️⃣ Roles for CHECKED SITE ONLY
      ===================================================== */
        const selectedSiteRoles: RoleModel[] =
          user?.roles?.filter(
            (role: RoleModel) => role?.locationId === checkedSiteId,
          ) ?? [];

        const hasUserRoleForSite = selectedSiteRoles?.length > 0;

        /* =====================================================
         2️⃣ ADMIN SITE ACCESS CHECK
      ===================================================== */
        let adminAccess$: Observable<boolean> = of(true);

        // COUNTRY ADMIN
        if (
          user?.adminRole === RoleTypeEnum?.CountryAdmin &&
          user?.countryAccessList?.length
        ) {
          const checks$ = user?.countryAccessList.map(
            (item: CountryAccessListModel) =>
              this.firebaseService
                .CheckCountryAdminSitesAccess(item?.opco_id, checkedSiteId)
                .pipe(map((sites) => sites?.length > 0)),
          );

          adminAccess$ =
            checks$.length > 0
              ? forkJoin<boolean[]>(checks$).pipe(
                  map((results) => results.some((result) => result)),
                )
              : of(false);
        } else if (
          user.adminRole === RoleTypeEnum.AreaHead &&
          user.regionAccessList.length
        ) {
          const checkedRegionId =
            user?.checkedInLocation?.region?.locationId ??
            user?.defaultLocation?.region?.locationId;

          const hasRegionAccess = user?.regionAccessList.some(
            (region: RegionAssignedModel) =>
              region?.unique_id === checkedRegionId,
          );

          adminAccess$ = of(hasRegionAccess);
        }
        // ADMIN
        else if (
          user?.adminRole === RoleTypeEnum?.Admin &&
          user?.plantAssigned?.length
        ) {
          adminAccess$ = of(
            user?.plantAssigned.some(
              (plant: PlantAssignedModel) => plant?.unique_id === checkedSiteId,
            ),
          );
        }

        /* =====================================================
         3️⃣ COMBINE ACCESS
      ===================================================== */
        return adminAccess$.pipe(
          map((hasAdminSiteAccess) => {
            const adminAccessObj = user?.adminAccess?.access ?? {};
            let combinedAccess: RoleAccessModel = {} as RoleAccessModel;

            /* ---------------- USER ---------------- */
            if (user?.adminRole === RoleTypeEnum?.User) {
              if (!hasUserRoleForSite) {
                return {} as RoleAccessModel;
              }

              const roleAccessSources = selectedSiteRoles.map(
                (role) => role?.access ?? {},
              );

              const keys = new Set<string>([
                ...Object.keys(adminAccessObj),
                ...roleAccessSources.flatMap((access) => Object.keys(access)),
              ]);

              keys.forEach((key) => {
                combinedAccess[key as keyof RoleAccessModel] =
                  roleAccessSources.some(
                    (access) => !!access?.[key as keyof RoleAccessModel],
                  ) || !!adminAccessObj?.[key as keyof RoleAccessModel];
              });
            } else if (user.adminRole === RoleTypeEnum.Admin) {
              /* ---------------- ADMIN ---------------- */
              combinedAccess = { ...adminAccessObj };

              if (!hasAdminSiteAccess) {
                combinedAccess.assetCreation = false;
                combinedAccess.assetEdition = false;
              }
            } else if (user?.adminRole === RoleTypeEnum?.CountryAdmin) {
              /* ---------------- COUNTRY ADMIN ---------------- */
              combinedAccess = { ...adminAccessObj };

              if (!hasAdminSiteAccess) {
                combinedAccess.assetCreation = false;
                combinedAccess.assetEdition = false;
              }
            } else {
              /* ---------------- DEFAULT ---------------- */
              combinedAccess = { ...adminAccessObj };
            }

            /* OVERRIDES (SITE-SCOPED) */
            const hasStructuralExpert = user.roles.some(
              (role: UserRolesModel) => role?.role === "structural_expert",
            );

            const hasApprovalRole = selectedSiteRoles.some(
              (role) =>
                role?.role === "unit_manager" || role?.role === "site_manager",
            );

            // Approval only if role exists for site
            combinedAccess.assetApprover =
              hasUserRoleForSite && hasApprovalRole;

            // Structural expert override
            if (hasStructuralExpert) {
              combinedAccess.assetCreation = true;
              combinedAccess.assetEdition = true;
            }
            return combinedAccess;
          }),
        );
      }),
    );
  }

  async GetAllUsers() {
    if (this.allUsersSubscription && !this.allUsersSubscription.closed) {
      this.allUsersSubscription.unsubscribe();
    }
    const userData = (await this.storage.get("user")) || {};
    const locationId =
      userData?.checkedInLocation?.unit?.locationId ??
      userData?.defaultLocation?.unit?.locationId;

    if (!locationId) {
      console.error(
        "User locationId not found, cannot fetch users for location.",
      );
      return;
    }
    this.allUsersSubscription = this.firebaseService
      .getDocumentsWithCondition(
        collection.users,
        "defaultLocation.unit.locationId",
        "==",
        locationId,
      )
      .subscribe({
        next: (users: UserModel[]) => {
          if (users) {
            const mappedUsers = users.map((user) => ({
              id: user?.id,
              email: user?.email,
              roles: user?.roles,
              contact: user?.contact,
              lastName: user?.lastName,
              firstName: user?.firstName,
              profileImage: user?.profileImage,
            }));
            if (this.networkService.getCurrentNetworkStatus()) {
              this.storage.set("users", mappedUsers);
            }
          }
        },
        error: (err) => {
          console.error("Error fetching users:", err);
        },
      });
  }
  navigateToDefaultTab(user: UserModel): void {
    const permissions = (user?.adminAccess?.permissions || []) as {
      access: {
        edit: boolean;
        approval: boolean;
        create: boolean;
        assignee: boolean;
        view: boolean;
      };
      moduleId: string;
      moduleName: string;
      parentId: string | null;
    }[];

    if (!permissions.length) {
      // fallback: go to dashboard if no permission set
      this.router.navigate(["/login"], { replaceUrl: true });
      return;
    }
    const currentUrl = window.location.pathname;
    if (currentUrl.startsWith("/energy-isolation/ei-pdf/")) {
      this.router.navigateByUrl(currentUrl, { replaceUrl: true });
      return;
    } else if (currentUrl.startsWith("/training")) {
      this.router.navigateByUrl(currentUrl, { replaceUrl: true });
      return;
    } else if ((currentUrl.startsWith("/settings") ||
        window.location.href.includes("/settings")) &&
      new URLSearchParams(window.location.search).get("tab") ===
        "material_management"
    ) {
      const params = new URLSearchParams(window.location.search);
      const materialId = params.get("materialId");

      if (currentUrl.startsWith("/settings/register") && materialId) {
        this.router.navigate(["/settings/register"], {
          queryParams: { materialId },
          replaceUrl: true,
        });
      } else {
        this.router.navigate(["/settings"], { replaceUrl: true });
      }
      return;
    } else {
      const moduleIds = permissions.map((p) => p.moduleId);
      const accessibleTabs = this.allTabs
        .filter((tab) => moduleIds.includes(tab.id))
        .sort((a, b) => a.order - b.order);

      if (accessibleTabs.length > 0) {
        const defaultTab = accessibleTabs[0];
        this.router.navigate([defaultTab.route], { replaceUrl: true });
      }
    }
  }

  getAccessibleTabs(user: UserModel): Tab[] {
    const permissions = (user?.adminAccess?.permissions || []) as {
      moduleId: string;
    }[];

    const moduleIds = permissions.map((p) => p.moduleId);
    return this.allTabs
      .filter((tab) => moduleIds.includes(tab.id))
      .map((tab) => ({
        ...tab,
        hasAccess: true,
      }));
  }
  async GetUserAssignedLocations(): Promise<string[]> {
    const user = await firstValueFrom(this.store.select("user"));

    let locationIds: string[] = [];

    if (user?.defaultLocation?.country?.locationId) {
      locationIds.push(user.defaultLocation.country.locationId);
    }

    const rolesLocations = user.roles
      .map((role: RoleModel) => role.locationId)
      .filter((locationId: string) => !!locationId);

    return [...locationIds, ...rolesLocations];
  }

  GetLoggedInUserSiteList() {
    from(this.GetUserAssignedLocations())
      .pipe(
        switchMap((locationIds) =>
          this.firebaseService.GetCountryAdminSites(locationIds),
        ),
      )
      .subscribe({
        error: (error) => {
          console.error("❌ Error fetching sites:", error);
        },
      });
  }

  async GetUserFilterByCountry(countryUniqueId: string, siteUniqueId: string) {
    try {
      this.firebaseService
        .getUsersByCountry(countryUniqueId, siteUniqueId)
        .subscribe({
          next: (users) => {
            this.mappedUsers.next(users);
          },
        });
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }

  hasUserAccessToModule(moduleId: string): Observable<boolean> {
    return this.store.select("user").pipe(
      switchMap((user) => {
        if (!user) {
          return of(false);
        }

        const checkedSiteId =
          user?.checkedInLocation?.site?.locationId ??
          user?.defaultLocation?.site?.locationId;

        let allowed = false;

        if (checkedSiteId) {
          const role = user.roles.find(
            (role: RoleModel) => role.locationId === checkedSiteId,
          );
          if (role?.permissions?.length) {
            allowed =
              allowed || this.hasViewAccess(role.permissions, moduleId);
          }
        }

        // country_security_expert keeps a country id, not a site id, and its tree is
        // left out of the adminAccess union (it is country-scoped): the menu is opened
        // from the role itself, the module then scopes view / edit from
        // getModuleAccess() / SecurityMappingAccessService
        for (const role of user.roles ?? []) {
          if (
            role?.role === RoleTypeEnum.CountrySecurityExpert &&
            role?.permissions?.length
          ) {
            allowed =
              allowed || this.hasViewAccess(role.permissions, moduleId);
          }
        }

        // Check adminAccess permissions (regardless of adminRole type or checked-in site)
        if (user.adminAccess?.permissions?.length) {
          allowed =
            allowed ||
            this.hasViewAccess(user.adminAccess.permissions, moduleId);
        }

        return of(allowed);
      }),
    );
  }

  hasViewAccess(
    permissions: RoleModuleAccessModel[],
    moduleId: string,
  ): boolean {
    return !!this.findModule(permissions, moduleId);
  }

  // The module node itself, wherever it sits in the tree (submodules included)
  findModule(
    permissions: RoleModuleAccessModel[],
    moduleId: string,
  ): RoleModuleAccessModel | undefined {
    for (const mod of permissions ?? []) {
      if (mod?.moduleId === moduleId) {
        return mod;
      }
      if (mod?.submodules?.length) {
        const found = this.findModule(mod.submodules, moduleId);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  // Access flags (view / edit / create / ...) for a module, OR-ed over the same
  // sources hasUserAccessToModule() looks at. Null when the module is not granted
  // at all. Note hasUserAccessToModule() only tests node presence, so a module can
  // be reachable while every flag here is false.
  getModuleAccess(
    moduleId: string,
  ): Observable<RoleMasterAccessListModel | null> {
    return this.store.select("user").pipe(
      map((user) => {
        if (!user) {
          return null;
        }

        const checkedSiteId =
          user?.checkedInLocation?.site?.locationId ??
          user?.defaultLocation?.site?.locationId;

        const sources: RoleModuleAccessModel[] = [];

        if (checkedSiteId) {
          const role = user.roles?.find(
            (role: RoleModel) => role.locationId === checkedSiteId,
          );
          const siteModule = role?.permissions?.length
            ? this.findModule(role.permissions, moduleId)
            : undefined;
          if (siteModule) {
            sources.push(siteModule);
          }
        }

        // Merged tree: adminRole + every role in roles[], regardless of site
        const mergedModule = user.adminAccess?.permissions?.length
          ? this.findModule(user.adminAccess.permissions, moduleId)
          : undefined;
        if (mergedModule) {
          sources.push(mergedModule);
        }

        if (!sources.length) {
          return null;
        }

        return sources.reduce(
          (merged, mod) => {
            const access = mod?.access ?? ({} as RoleMasterAccessListModel);
            for (const key of Object.keys(access) as (keyof RoleMasterAccessListModel)[]) {
              merged[key] = merged[key] || !!access[key];
            }
            return merged;
          },
          {
            view: false,
            edit: false,
            delete: false,
            create: false,
            export: false,
            approval: false,
            assignee: false,
          } as RoleMasterAccessListModel,
        );
      }),
    );
  }

  private isUser(): boolean {
    let isUser = false;
    this.store
      .select("user")
      .pipe(take(1))
      .subscribe((user) => {
        isUser = !!user?.email;
      });
    return isUser;
  }

  async CheckUserRoleExistByRoleName(role: string): Promise<boolean> {
    let isExist = false;

    this.store
      .select("user")
      .pipe(take(1))
      .subscribe((user: UserModel) => {
        const isAdminMatch = user?.adminRole && user.adminRole === role;

        // For MM Expert role, don't check locationId
        const isRoleMatch =
          role === RoleTypeEnum.MMExpert
            ? user?.roles?.some(
                (userRole: UserRolesModel) => userRole?.role === role,
              )
            : user?.roles?.some(
                (roleUser: UserRolesModel) =>
                  roleUser?.role === role &&
                  roleUser.locationId ===
                    (user?.checkedInLocation?.site?.locationId ||
                      user?.defaultLocation?.site?.locationId),
              );

        isExist = isAdminMatch || isRoleMatch || false;
      });

    return isExist;
  }
}
